import { MapPin, Plus } from 'lucide-react'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import {
  ShippingLocation_,
  useShippingLocationsApi,
} from 'src/api/db/apiShippingLocations'
import { AddEditShippingLocationDialog } from 'src/shared/components/organizationPage/ShippingLocations/components/AddEditShippingLocationDialog'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOptionalRequestContext } from 'src/pages/_myAccount/Requests/Request/hooks/RequestContext'
import { ShippingLocationCard } from './components/ShippingLocationCard'

export const Destinations = ({
  totalItems,
  quote_id,
  selectedLocationIds = [],
  onToggleLocation,
  locationNotesByShippingLocationId,
  onLocationNotesChange,
  onShippingLocationDeleted,
}: {
  totalItems: number
  quote_id?: number | null
  selectedLocationIds?: number[]
  onToggleLocation?: (locationId: number) => void
  locationNotesByShippingLocationId: Record<number, string>
  onLocationNotesChange: Dispatch<SetStateAction<Record<number, string>>>
  onShippingLocationDeleted?: (locationId: number) => void
}) => {
  void totalItems
  void quote_id
  const { t } = useLocalization()
  const { isDemo, demoShippingLocations } = useOptionalRequestContext() ?? {}
  const org_id = useGetOrgId()
  const { shippingLocations, isLoading, deleteShippingLocation } =
    useShippingLocationsApi({
      org_id: isDemo ? undefined : org_id || undefined,
    })

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingLocation, setEditingLocation] = useState<
    ShippingLocation_ | undefined
  >()
  const didAutoSelectRef = useRef(false)

  const activeLocations = (
    isDemo ? demoShippingLocations ?? [] : shippingLocations || []
  ).filter((loc) => loc.active)
  const hasLocations = activeLocations.length > 0
  const isMultipleDestinations = selectedLocationIds.length > 1

  useEffect(() => {
    if (didAutoSelectRef.current) return
    if (!onToggleLocation) return
    if (selectedLocationIds.length > 0) {
      didAutoSelectRef.current = true
      return
    }
    if (activeLocations.length === 0) return
    const preferred =
      activeLocations.find((loc) => loc.default) ?? activeLocations[0]
    didAutoSelectRef.current = true
    onToggleLocation(preferred.id)
  }, [activeLocations, onToggleLocation, selectedLocationIds.length])

  const handleAddDestination = () => {
    setEditingLocation(undefined)
    setIsDialogOpen(true)
  }

  const handleEditLocation = (location: ShippingLocation_) => {
    setEditingLocation(location)
    setIsDialogOpen(true)
  }

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open)
    if (!open) setEditingLocation(undefined)
  }

  const handleDeleteLocation = async (location: ShippingLocation_) => {
    if (
      !window.confirm(
        t(
          'Are you sure you want to delete this location?',
          'Är du säker på att du vill ta bort denna plats?'
        )
      )
    ) {
      return
    }
    try {
      await deleteShippingLocation.mutateAsync(location.id)
      onShippingLocationDeleted?.(location.id)
      onLocationNotesChange((prev) => {
        const next = { ...prev }
        delete next[location.id]
        return next
      })
    } catch {
      // Toast handled by mutation onError
    }
  }

  if (!isDemo && isLoading) {
    return (
      <Column className="gap-1 py-2">
        <p className="text-sm text-muted-foreground">
          {t('Loading destinations...', 'Laddar destinationer...')}
        </p>
      </Column>
    )
  }

  return (
    <Column className="gap-3">
      <Column className="gap-1">
        <Row className="items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <h3>{t('Shipping', 'Leverans')}</h3>
        </Row>
        <p className="text-sm text-muted-foreground">
          {hasLocations
            ? t(
                'Select where we should ship this order.',
                'Välj vart vi ska skicka den här ordern.'
              )
            : t(
                'Add where we should ship this order.',
                'Lägg till vart vi ska skicka den här ordern.'
              )}
        </p>
      </Column>

      {isMultipleDestinations ? (
        <p className="text-sm text-muted-foreground">
          {t(
            'Several addresses selected — note which items go where.',
            'Flera adresser valda — ange vilka artiklar som ska vart.'
          )}
        </p>
      ) : null}

      {!hasLocations && !isDemo ? (
        <button
          type="button"
          onClick={handleAddDestination}
          className="flex w-full items-center justify-between gap-3 rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-3.5 text-left transition-colors hover:border-gray-400 hover:bg-gray-50"
        >
          <Column className="min-w-0 gap-0.5">
            <span className="text-sm font-medium text-foreground">
              {t('Add shipping address', 'Lägg till leveransadress')}
            </span>
            <span className="text-sm text-muted-foreground">
              {t(
                'Company, street, city, and contact.',
                'Företag, gata, stad och kontakt.'
              )}
            </span>
          </Column>
          <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      ) : (
        <Column className="gap-2">
          {activeLocations.map((location) => (
            <ShippingLocationCard
              key={location.id}
              location={location}
              onEdit={isDemo ? undefined : handleEditLocation}
              onDelete={
                isDemo || !org_id ? undefined : handleDeleteLocation
              }
              deleteActionDisabled={deleteShippingLocation.isPending}
              isSelected={selectedLocationIds.includes(location.id)}
              onSelect={
                isDemo || !onToggleLocation
                  ? undefined
                  : () => onToggleLocation(location.id)
              }
              showItemInstructions={isMultipleDestinations}
              itemInstructionsValue={
                locationNotesByShippingLocationId[location.id] ?? ''
              }
              onItemInstructionsChange={(value) =>
                onLocationNotesChange((prev) => ({
                  ...prev,
                  [location.id]: value,
                }))
              }
            />
          ))}

          {isDemo ? null : (
            <Button
              variant="ghost"
              size="sm"
              className="w-fit"
              onClick={handleAddDestination}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              {t('Add another address', 'Lägg till en adress till')}
            </Button>
          )}
        </Column>
      )}

      {org_id && !isDemo && (
        <AddEditShippingLocationDialog
          isModalOpen={isDialogOpen}
          setIsModalOpen={handleDialogClose}
          editLocation={editingLocation}
          org_id={org_id}
          onShippingLocationCreated={(id) => onToggleLocation?.(id)}
        />
      )}
    </Column>
  )
}
